import React from 'react'
import { Card, Code, Group, Modal, Stack, Table, Tabs, Text } from "@mantine/core"

type MatchSummayModalProps = {
  opened: boolean
  onClose: () => void
  match: any
  matchSummary: any
  jobMatches: any[]
}

const CompTable = ({ source, removeMarketPrefix }: { source: Record<string, number | null>, removeMarketPrefix?: boolean }) => {
  const marketElements = [
    { label: 'Base Salary', fieldCode: 'market_base_salary' },
    { label: 'Total Cash Paid', fieldCode: 'market_total_cash_paid' },
    { label: 'Target Incentive', fieldCode: 'market_target_incentive' },
    { label: 'LTI Dollars', fieldCode: 'market_lti_dollars' },
    { label: 'Total Target Cash', fieldCode: 'market_ttc' },
    { label: 'Total Direct', fieldCode: 'market_total_direct' },
    { label: 'TTD', fieldCode: 'market_ttd' },
    { label: 'Market Avg', fieldCode: 'market_avg' },
  ]

  const elements = removeMarketPrefix ?
    marketElements.map(me => ({ label: me.label, fieldCode: me.fieldCode.substring(7) })) :
    marketElements

  return (
    <Table style={{ tableLayout: 'fixed' }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Comp Element</Table.Th>
          <Table.Th>10th</Table.Th>
          <Table.Th>25th</Table.Th>
          <Table.Th>50th</Table.Th>
          <Table.Th>Avg</Table.Th>
          <Table.Th>75th</Table.Th>
          <Table.Th>90th</Table.Th>
          <Table.Th>Min</Table.Th>
          <Table.Th>Mid</Table.Th>
          <Table.Th>Max</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {elements.map(me => (
        <Table.Tr>
          <Table.Td>{me.label}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_10th`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_25th`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_50th`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_avg`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_75th`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_90th`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_min`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_mid`] || '-'}</Table.Td>
          <Table.Td>{source[`${me.fieldCode}_max`] || '-'}</Table.Td>
        </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>

  )
}

export const MatchSummaryModal = ({ opened, onClose, match, matchSummary, jobMatches }: MatchSummayModalProps) => {
  if (!match || !matchSummary || !jobMatches) return

  return (
    <Modal size='75vw' opened={opened} onClose={onClose}>
      <Group>
        <Text size='lg'>{match.org_job_code}</Text>
        <Text size='lg'>{match.org_job_code_key}</Text>
      </Group>
      <Tabs defaultValue="matchInfo">
        <Tabs.List>
          <Tabs.Tab value="matchInfo">
            Match Info
          </Tabs.Tab>
          <Tabs.Tab value="jobMatches">
            Job Matches
          </Tabs.Tab>
          <Tabs.Tab value="matchSummary">
            Match Summary
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="matchInfo">
          <Stack my='lg'>
            <Card withBorder shadow='xl' p='xl'>
              <Stack>
                  <Group>
                    <Text>Survey Job Code: <Text span fw='bold'>{match.survey_job_code}</Text></Text>
                    <Text>Survey Code: <Text span fw='bold'>{match.survey_code}</Text></Text>
                    <Text>Data Cut: <Text span fw='bold'>{match.job_scope}</Text></Text>
                    <Text>Survey Year: <Text span fw='bold'>{match.survey_year}</Text></Text>
                  </Group>
                  <Group>
                    <Text>Weighting: <Text span fw='bold'>{match.data_weighting_factor}</Text></Text>
                    <Text>Adjustment: <Text span fw='bold'>{match.data_adjustment_factor}</Text></Text>
                    <Text>Aging: <Text span fw='bold'>{match.data_adjustment_factor}</Text></Text>
                    <Text>Geo Adjustment: <Text span fw='bold'>{match.data_adjustment_factor}</Text></Text>
                    <Text>Companies: <Text span fw='bold'>{match.total_companies}</Text></Text>
                    <Text>Incumbents: <Text span fw='bold'>{match.total_incumbents}</Text></Text>
                  </Group>
                <CompTable source={match} removeMarketPrefix />
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="jobMatches">
          <Stack my='lg'>
            { jobMatches.map(jm => (
              <Card withBorder p='xl' shadow='xl' key={jm.match_id}>
                <Stack>
                  <Group>
                    <Text>Survey Job Code: <Text span fw='bold'>{jm.survey_job_code}</Text></Text>
                    <Text>Survey Code: <Text span fw='bold'>{jm.survey_code}</Text></Text>
                    <Text>Data Cut: <Text span fw='bold'>{jm.job_scope}</Text></Text>
                    <Text>Survey Year: <Text span fw='bold'>{jm.survey_year}</Text></Text>
                  </Group>
                  <Group>
                    <Text>Weighting: <Text span fw='bold'>{jm.data_weighting_factor}</Text></Text>
                    <Text>Adjustment: <Text span fw='bold'>{jm.data_adjustment_factor}</Text></Text>
                    <Text>Aging: <Text span fw='bold'>{jm.data_adjustment_factor}</Text></Text>
                    <Text>Geo Adjustment: <Text span fw='bold'>{jm.data_adjustment_factor}</Text></Text>
                    <Text>Companies: <Text span fw='bold'>{jm.total_companies}</Text></Text>
                    <Text>Incumbents: <Text span fw='bold'>{jm.total_incumbents}</Text></Text>
                  </Group>
                  <CompTable source={jm} removeMarketPrefix />
                </Stack>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="matchSummary">
          <Stack my='lg'>
            <Card withBorder p='xl' shadow='xl'>
              <Stack>
                <Group>
                  <Text>Matches: </Text><Text span fw='bold'>{matchSummary.num_matches}</Text>
                  <Text>Total Companies: </Text><Text span fw='bold'>{matchSummary.total_companies}</Text>
                  <Text>Total Incumbents: </Text><Text span fw='bold'>{matchSummary.total_incumbents}</Text>
                </Group>
                <CompTable source={matchSummary} />
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  )
}
